import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

/**
 * The page's `t`. A key that a locale hasn't translated yet falls back to the
 * English key with its {{placeholders}} filled in: next-translate otherwise
 * returns a missing key raw, placeholders and all.
 */
export function useB2bTranslation() {
  const { t, lang } = useLocale('b2b');
  return {
    lang,
    t: (key: string, query?: Record<string, unknown>) =>
      t(key, query, { default: key }),
  };
}

/**
 * The fictional company in the page's product mock-ups. Its workspace mark
 * uses its own colour, not ours, like the demo businesses on /chatbots.
 */
export const COMPANY = 'Kális';
export const COMPANY_MARK = 'bg-[#1d1c26] dark:bg-[#2b2a38]';

/** Security and privacy standards Easy-Peasy.AI meets (see the Trust Center). */
export const COMPLIANCE = ['SOC 2', 'ISO 27001', 'GDPR', 'HIPAA'];

export type Teammate = {
  name: string;
  initials: string;
  /** Tailwind background class for the avatar. */
  color: string;
};

// Names are people, so they are never translated.
export const TEAM = {
  maya: { name: 'Maya Chen', initials: 'MC', color: 'bg-[#d9764a]' },
  daniel: { name: 'Daniel Okafor', initials: 'DO', color: 'bg-[#2f7d6d]' },
  sofia: { name: 'Sofia Reyes', initials: 'SR', color: 'bg-[#7a5bd4]' },
  arjun: { name: 'Arjun Mehta', initials: 'AM', color: 'bg-[#c48b16]' },
  lena: { name: 'Lena Fischer', initials: 'LF', color: 'bg-[#c2416c]' },
  tom: { name: 'Tom Becker', initials: 'TB', color: 'bg-[#3d6fd1]' },
} satisfies Record<string, Teammate>;

export function Avatar({
  person,
  size = 'md',
  className,
}: {
  person: Teammate;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  return (
    <span
      aria-hidden='true'
      className={clsxm(
        'flex flex-none items-center justify-center rounded-full font-semibold text-white',
        size === 'sm' && 'h-6 w-6 text-[10px]',
        size === 'md' && 'h-8 w-8 text-[11px]',
        size === 'lg' && 'h-10 w-10 text-[13px]',
        person.color,
        className,
      )}
    >
      {person.initials}
    </span>
  );
}

/** Overlapping avatars with a "+N" chip, as in a shared document's header. */
export function AvatarStack({
  people,
  more,
  ringClass = 'ring-white dark:ring-[#16151f]',
}: {
  people: Teammate[];
  more?: number;
  ringClass?: string;
}) {
  return (
    <span className='flex items-center -space-x-2 rtl:space-x-reverse'>
      {people.map((person) => (
        <Avatar
          key={person.initials}
          person={person}
          size='sm'
          className={clsxm('ring-2', ringClass)}
        />
      ))}
      {more ? (
        <span
          className={clsxm(
            'flex h-6 min-w-6 items-center justify-center rounded-full bg-[#ececf3] px-1.5 text-[10px] font-semibold text-[#56566d] ring-2 dark:bg-[#26253a] dark:text-[#b7b7d2]',
            ringClass,
          )}
          dir='ltr'
        >
          +{more}
        </span>
      ) : null}
    </span>
  );
}

export function ShieldGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 20 20'
      fill='currentColor'
      aria-hidden='true'
      className={clsxm('h-4 w-4 flex-none', className)}
    >
      <path
        fillRule='evenodd'
        d='M9.661 2.237a.531.531 0 01.678 0 11.947 11.947 0 007.078 2.749.5.5 0 01.479.425c.069.52.104 1.05.104 1.59 0 5.162-3.26 9.563-7.834 11.256a.48.48 0 01-.332 0C5.26 16.564 2 12.163 2 7c0-.538.035-1.069.104-1.589a.5.5 0 01.48-.425 11.947 11.947 0 007.077-2.75zm4.196 5.954a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z'
        clipRule='evenodd'
      />
    </svg>
  );
}

export function SparkGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 20 20'
      fill='currentColor'
      aria-hidden='true'
      className={clsxm('h-4 w-4 flex-none', className)}
    >
      <path d='M10 1.5c.4 0 .75.27.85.66l.8 3.06a3.5 3.5 0 002.5 2.5l3.06.8a.88.88 0 010 1.7l-3.06.8a3.5 3.5 0 00-2.5 2.5l-.8 3.06a.88.88 0 01-1.7 0l-.8-3.06a3.5 3.5 0 00-2.5-2.5l-3.06-.8a.88.88 0 010-1.7l3.06-.8a3.5 3.5 0 002.5-2.5l.8-3.06A.88.88 0 0110 1.5z' />
    </svg>
  );
}
